/**
 * useEingangMails / useEingangDetail — TanStack Query hooks for the T5 Eingang
 * mail-split (Story V10-J).
 *
 * live mode → GET /tasks (list) + GET /tasks/:id (selected detail overlay).
 * mock mode → resolves the existing EINGANG mock (mock layer NOT deleted);
 *             the detail query is disabled (mock items already carry full content).
 *
 * V10-J2 additions:
 *   useAusgangMails(bucket)  — Ausgang/Gesendet tabs via GET /communications
 *                              (AUSGEHEND mock fallback).
 *   useEingangDecision()     — bestätigen/archivieren→POST /tasks/:id/complete ·
 *                              ablehnen→POST /tasks/:id/cancel, with an OPTIMISTIC
 *                              list update (+ rollback on error).
 *
 * Migration 0102 addition:
 *   useCommDismiss()         — ablehnen/archivieren→POST /communications/:commId/dismiss
 *                              (persists the triage dismissal server-side; fires ALONGSIDE
 *                              useEingangDecision's task cancel/complete, not instead of it).
 *                              OPTIMISTIC row removal from the list cache (+ rollback on error).
 *
 * [Source: apps/web/src/features/communication/hooks/useInbox.ts] (hook pattern)
 * [Source: apps/web/src/features/communication/hooks/useTaskActions.ts] (mutation pattern)
 * [Source: docs/stories/v10-j-web-wiring-core.md AC2]
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC6]
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommunicationContentDto, SplitViewResponse } from '@brokly/shared';
import type { CommOverviewBucket } from '../../../lib/api-client';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { taskActionErrorMessage } from '../../../lib/task-action-error';
import { isLiveData } from '../../../lib/data-mode';
import { fetchEingangInbox, fetchCommSplitView, fetchAusgangMails } from '../api/eingang-api';
import { AUSGEHEND, EINGANG } from '../mock';
import type { AusgangMail, EingangMail, VisibilityTier } from '../types';

/**
 * The Eingang inbox — PART A3: EVERY inbound communication (incl. internal /
 * no-task) via GET /communications?bucket=eingang, or the mock.
 *
 * NOTE: the live query key is kept as `inbox({surface:'eingang',mode:'live'})`
 * so useEingangDecision's optimistic cache update keeps targeting this list even
 * though the source endpoint moved from /tasks to /communications.
 */
export function useEingangMails() {
  const live = isLiveData();
  return useQuery<EingangMail[]>({
    queryKey: qk.inbox({ surface: 'eingang', mode: live ? 'live' : 'mock' }),
    queryFn: () => (live ? fetchEingangInbox() : Promise.resolve(EINGANG)),
    // mock mode: synchronous initialData (no loading flicker, deterministic tests)
    ...(live ? {} : { initialData: EINGANG, staleTime: Infinity }),
  });
}

/**
 * The split-view body for the selected Eingang COMMUNICATION (PART A3).
 *
 * The row id is a communication id, so the body is resolved via the comm's linked
 * task (GET /tasks?comm_id=… → GET /tasks/:id — the only body source). Only fetched
 * for a FULL-tier row that actually has a task; reduced-tier rows render the overlay
 * from row data (no body), and no-task rows have no body source (→ null).
 */
export function useEingangCommDetail(
  commId: string | null,
  opts: { tier?: VisibilityTier; hasTask?: boolean } = {},
) {
  const live = isLiveData();
  const tier = opts.tier ?? 'full';
  const enabled = live && commId !== null && tier === 'full' && opts.hasTask === true;
  return useQuery<SplitViewResponse | null>({
    queryKey: qk.commDetail(commId ?? 'none'),
    queryFn: () => fetchCommSplitView(commId as string),
    enabled,
  });
}

/**
 * The tier-projected body of the selected Eingang COMMUNICATION (Fix C).
 *
 * Unlike useEingangCommDetail (which resolves the body via a LINKED TASK, so no-task
 * comms never got a body), this hits GET /communications/:commId/content directly, so
 * ANY comm (task-linked or not) yields its body. HTML-only mail returns body_html.
 * Only fetched for a FULL-tier row; reduced tiers render the overlay (no body fetch).
 */
export function useEingangCommContent(
  commId: string | null,
  opts: { tier?: VisibilityTier } = {},
) {
  const live = isLiveData();
  const tier = opts.tier ?? 'full';
  const enabled = live && commId !== null && tier === 'full';
  return useQuery<CommunicationContentDto | null>({
    queryKey: qk.commContent(commId ?? 'none'),
    queryFn: () => apiClient.communications.getContent(commId as string),
    enabled,
  });
}

/**
 * The tier-projected body of a SENT/outbound communication (N2).
 *
 * The Ausgang/Gesendet list carries no body; the detail dialog fetches it here via
 * GET /communications/:commId/content — the SAME endpoint the Eingang uses, so the
 * server-side cid: logo inlining + tier projection are reused (no new API surface).
 * Only fetched in live mode for an open detail with a comm id.
 */
export function useOutboundContent(commId: string | null) {
  const live = isLiveData();
  const enabled = live && commId !== null;
  return useQuery<CommunicationContentDto | null>({
    queryKey: qk.commContent(commId ?? 'none'),
    queryFn: () => apiClient.communications.getContent(commId as string),
    enabled,
  });
}

/**
 * One outbound tab (V10-J2): 'ausgang_entwuerfe' (Ausgang) | 'gesendet' (Gesendet).
 * live → GET /communications?bucket=…; mock → AUSGEHEND filtered by tab semantics.
 */
export function useAusgangMails(bucket: Exclude<CommOverviewBucket, 'eingang'>) {
  const live = isLiveData();
  const mockRows = AUSGEHEND.filter((m) =>
    bucket === 'gesendet' ? m.status === 'gesendet' : m.status !== 'gesendet',
  );
  return useQuery<AusgangMail[]>({
    queryKey: qk.communications(bucket, { mode: live ? 'live' : 'mock' }),
    queryFn: () => (live ? fetchAusgangMails(bucket) : Promise.resolve(mockRows)),
    // mock mode: synchronous initialData (no loading flicker, deterministic tests)
    ...(live ? {} : { initialData: mockRows, staleTime: Infinity }),
  });
}

/** The decision actions wired to real task mutations (V10-J2). */
export type EingangDecisionAction = 'bestaetigen' | 'archivieren' | 'ablehnen';

/**
 * Eingang decision mutation (V10-J2, live mode):
 *   bestätigen / archivieren → POST /tasks/:id/complete
 *   ablehnen                 → POST /tasks/:id/cancel
 *
 * PART A3: the Eingang row id is now a COMMUNICATION id, but task actions need the
 * TASK id — the caller passes both (`taskId` resolved from the split-view detail;
 * `commId` = the row id used to key the optimistic cache update). A row with no
 * resolved task (internal / no-task, or detail not yet loaded) is a no-op.
 *
 * OPTIMISTIC: the list cache flips the item's triage to 'beantwortet' in onMutate;
 * onError rolls the snapshot back (the caller surfaces the error toast); onSettled
 * invalidates the live list. The UI renders the SERVER's state.
 */
export function useEingangDecision(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  const listKey = qk.inbox({ surface: 'eingang', mode: 'live' });

  return useMutation({
    mutationFn: ({ taskId, action }: { taskId: string; commId: string; action: EingangDecisionAction }) =>
      action === 'ablehnen' ? apiClient.tasks.cancel(taskId) : apiClient.tasks.complete(taskId),

    onMutate: async ({ commId }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<EingangMail[]>(listKey);
      qc.setQueryData<EingangMail[]>(listKey, (old) =>
        old?.map((m) => (m.id === commId ? { ...m, triage: 'beantwortet' as const } : m)),
      );
      return { previous };
    },

    onError: (error, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(listKey, context.previous);
      }
      // Keep the api's own reason: complete refuses a staged task or one carrying a held
      // action, and it names the Gesamtfreigabe as the place to close it.
      const reason = taskActionErrorMessage(error, 'Aktion fehlgeschlagen.');
      onErrorToast?.(`${reason} Änderung zurückgesetzt.`);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });
}

/**
 * Comm dismiss mutation (migration 0102, live mode): persists the Eingang
 * "Ablehnen"/"Archivieren" triage dismissal server-side — POST /communications/:commId/dismiss.
 *
 * FIX: dismissal used to be a client-only React state Set (EingangScreen.tsx
 * `dismissedIds`) that reset on reload/re-login. This mutation is the durable
 * counterpart — the caller fires it ALONGSIDE useEingangDecision's task cancel/complete
 * call (not instead of it), so both the task AND the comm's queue membership update.
 *
 * OPTIMISTIC: the row is removed from the SAME live Eingang list cache key
 * useEingangDecision targets (`inbox({surface:'eingang',mode:'live'})`) in onMutate;
 * onError rolls the snapshot back (the caller surfaces the error toast) — restoring the
 * row is the correct behaviour on failure since the server never actually dismissed it;
 * onSettled invalidates so the list re-syncs to the server's `dismissed_at IS NULL` filter.
 */
export function useCommDismiss(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  const listKey = qk.inbox({ surface: 'eingang', mode: 'live' });

  return useMutation({
    mutationFn: (commId: string) => apiClient.communications.dismiss(commId),

    onMutate: async (commId: string) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<EingangMail[]>(listKey);
      qc.setQueryData<EingangMail[]>(listKey, (old) => old?.filter((m) => m.id !== commId));
      return { previous };
    },

    onError: (_error, _commId, context) => {
      if (context?.previous) {
        qc.setQueryData(listKey, context.previous);
      }
      onErrorToast?.('Ablehnen fehlgeschlagen, Änderung zurückgesetzt.');
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });
}

/**
 * Privat-Markierung mutation (Berechtigungskonzept V3.0 §2.2, Bereich 1) — mark/unmark
 * a communication private via POST /communications/:commId/mark-private|unmark-private.
 *
 * Unlike useCommDismiss, the row is NOT removed from the caller's own list — the sender
 * / internal to/cc recipient who (un)marks it keeps full access (only OTHER internal
 * users lose visibility, server-side). The mutation just invalidates the live Eingang
 * list + the comm's content cache so the "Privat" badge and the action's label
 * ("Privat markieren" / "Nicht mehr privat") refresh from the server's state.
 */
export function useCommPrivateMark(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  const listKey = qk.inbox({ surface: 'eingang', mode: 'live' });

  return useMutation({
    mutationFn: ({ commId, makePrivate }: { commId: string; makePrivate: boolean }) =>
      makePrivate
        ? apiClient.communications.markPrivate(commId)
        : apiClient.communications.unmarkPrivate(commId),

    onSuccess: (_result, { commId }) => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: qk.commContent(commId) });
    },

    onError: () => {
      onErrorToast?.('Aktion fehlgeschlagen.');
    },
  });
}

/**
 * Berechtigungskonzept V3.0 P4 — mark/unmark a whole e-mail THREAD private via
 * POST /communications/:commId/mark-thread-private|unmark-thread-private. Flips every mail
 * in the thread (past + current) and auto-privates future mail. The whole list + the comm's
 * content cache are invalidated so the "Privat" badges + the toggle labels refresh from the
 * server (a thread mark can flip MANY rows the caller cannot see, so we refetch broadly).
 */
export function useCommThreadPrivateMark(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  const listKey = qk.inbox({ surface: 'eingang', mode: 'live' });

  return useMutation({
    mutationFn: ({ commId, makePrivate }: { commId: string; makePrivate: boolean }) =>
      makePrivate
        ? apiClient.communications.markThreadPrivate(commId)
        : apiClient.communications.unmarkThreadPrivate(commId),

    onSuccess: (_result, { commId }) => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: qk.commContent(commId) });
    },

    onError: () => {
      onErrorToast?.('Aktion fehlgeschlagen.');
    },
  });
}
